import { motorNaoTratado, type Motor } from '@remoto/protocolo';

import { pesoDoUsoOpencode, type UsoOpencode } from '../opencode/custo';

/**
 * Preço, peso e limite de contexto por modelo (ADR-0021).
 *
 * **Não é fatura.** A conta do Claude é assinatura: ninguém paga por token, o
 * que limita é a cota semanal. O custo equivalente em USD existe para uma coisa
 * só — dar a cada chamada um PESO comparável, para ratear o % semanal da conta
 * entre as sessões (`rateio.ts`). Somar tokens crus poria no mesmo prato um
 * token de Fable e um de Haiku, que custam cotas muito diferentes.
 *
 * O Codex não tem tabela de preço aqui: o peso dele é uma fórmula em tokens
 * (`PESO_CODEX`), decidida pelo dono. Os dois pesos NUNCA se somam — o rateio
 * é por motor, porque cada motor é uma conta.
 */

export interface PrecoPorMTok {
  entrada: number;
  saida: number;
  cacheLido: number;
  cacheEscrito: number;
  /**
   * Preço que vale quando o prompt da chamada (entrada + cache lido + cache escrito) passa de
   * `acimaDeTokens`. Só o Haiku 5.5 tem degrau hoje: US$ 0,10/0,50 até 100 mil, US$ 0,50/2,50 acima.
   */
  degrau?: { acimaDeTokens: number; preco: Omit<PrecoPorMTok, 'degrau'> };
}

/** USD por milhão de tokens, por modelo da API. A tabela é a da Interface congelada do plano. */
export const PRECOS_POR_MTOK: Readonly<Record<string, PrecoPorMTok>> = {
  // Opus 5.5: claude.com/pricing, conferido em 2026-09-24 (o 8/40 que aparece no binário do
  // Claude Code é o do modo rápido, "2x preço padrão").
  'claude-opus-5-5': { entrada: 4, saida: 20, cacheLido: 0.2, cacheEscrito: 5 },
  'claude-opus-5': { entrada: 5, saida: 25, cacheLido: 0.5, cacheEscrito: 6.25 },
  'claude-opus-4-8': { entrada: 5, saida: 25, cacheLido: 0.5, cacheEscrito: 6.25 },
  'claude-fable-5-1': { entrada: 10, saida: 50, cacheLido: 0.25, cacheEscrito: 12.5 },
  'claude-fable-5': { entrada: 10, saida: 50, cacheLido: 1, cacheEscrito: 12.5 },
  'claude-sonnet-5': { entrada: 2, saida: 10, cacheLido: 0.2, cacheEscrito: 2.5 },
  // Sonnet 5.5 e Haiku 5.5: tabela da skill claude-api (2026-10-06). Até 2026-11-04 faltavam aqui, e
  // o peso deles caía em zero: a tela de Uso mostrava 60-72% do gasto real (plano de redução de tokens, R1).
  'claude-sonnet-5-5': { entrada: 2, saida: 10, cacheLido: 0.2, cacheEscrito: 2.5 },
  'claude-haiku-5-5': {
    entrada: 0.1,
    saida: 0.5,
    cacheLido: 0.01,
    cacheEscrito: 0.125,
    degrau: { acimaDeTokens: 100_000, preco: { entrada: 0.5, saida: 2.5, cacheLido: 0.05, cacheEscrito: 0.625 } },
  },
  'claude-haiku-4-5': { entrada: 1, saida: 5, cacheLido: 0.1, cacheEscrito: 1.25 },
};

/**
 * O peso de uma chamada do Codex, em "tokens equivalentes": entrada nova conta
 * inteira, entrada em cache conta um décimo, saída conta quatro vezes.
 */
export const PESO_CODEX = { cache: 0.1, saida: 4 } as const;

/**
 * O tamanho da janela de contexto por modelo do Claude. O Codex não entra aqui:
 * o limite dele vem do `contextWindow` do próprio evento `uso-do-motor`.
 */
export const LIMITE_DE_CONTEXTO: Readonly<Record<string, number>> = {
  'claude-opus-5-5': 1_000_000,
  'claude-opus-5': 1_000_000,
  'claude-opus-4-8': 1_000_000,
  'claude-fable-5-1': 1_000_000,
  'claude-fable-5': 1_000_000,
  'claude-sonnet-5': 1_000_000,
  'claude-sonnet-5-5': 1_000_000,
  // O Haiku 4.5 tem 200k, e não 1M: escrever 1M aqui faria o cabeçalho da
  // sessão mostrar folga que não existe. O Haiku 5.5 já tem 1M.
  'claude-haiku-4-5': 200_000,
  'claude-haiku-5-5': 1_000_000,
};

/**
 * O id do modelo sem o carimbo de data do snapshot (`claude-haiku-4-5-20251001`
 * → `claude-haiku-4-5`). É o mesmo modelo, com o mesmo preço; sem isto o
 * Haiku, que o transcript grava com data, cairia em "desconhecido".
 */
export function modeloSemData(modelo: string): string {
  return modelo.replace(/-\d{8}$/, '');
}

export function precoDoModelo(modelo: string | null): PrecoPorMTok | null {
  if (modelo === null) return null;
  return PRECOS_POR_MTOK[modeloSemData(modelo)] ?? null;
}

export function limiteDeContexto(modelo: string | null): number | null {
  if (modelo === null) return null;
  return LIMITE_DE_CONTEXTO[modeloSemData(modelo)] ?? null;
}

/** O `message.usage` do Claude. `input_tokens` NÃO inclui o cache (convenção da Anthropic). */
export interface UsoClaude {
  input_tokens?: number;
  output_tokens?: number;
  cache_read_input_tokens?: number;
  cache_creation_input_tokens?: number;
  /**
   * A parte de `cache_creation_input_tokens` escrita com TTL de 1 h
   * (`usage.cache_creation.ephemeral_1h_input_tokens`), cobrada a 2x a entrada. Ausente é "o
   * transcript não separou": tudo conta como 5 min (1,25x), o comportamento anterior.
   */
  cache_creation_1h_input_tokens?: number;
}

/** O `usage` do evento `uso-do-motor`. `input_tokens` INCLUI o cache (convenção da OpenAI). */
export interface UsoCodex {
  input_tokens: number;
  cached_input_tokens: number;
  output_tokens: number;
}

export interface PesoDoUso {
  peso: number;
  /** Só no Claude, e só com preço conhecido. Nunca inventado. */
  custoEquivUsd: number | null;
}

const MILHAO = 1_000_000;

/** A escrita de cache de 1 h custa 2x a entrada (a de 5 min, 1,25x, já está em `cacheEscrito`). */
const FATOR_ESCRITA_1H = 2;

/**
 * O peso de UMA chamada, para o rateio.
 *
 * - **CLAUDE**: peso = custo equivalente em USD. Modelo sem preço conhecido dá
 *   `peso: 0` e `custoEquivUsd: null` — nunca um preço inventado, e nunca os
 *   tokens crus no lugar: misturar tokens com dólar na mesma soma distorceria
 *   o rateio da conta inteira. Hoje o único caso real é `<synthetic>`, que o
 *   CLI grava com usage zerado, então o peso zero não esconde consumo.
 * - **CODEX**: `(entrada − cache) + 0,1 × cache + 4 × saída`, sem USD. A
 *   entrada nova nunca fica negativa, mesmo que o motor mande cache maior que
 *   o total.
 * - **OPENCODE** (F16): o custo REAL que o motor relatou, em US$ — a regra
 *   mora em `opencode/custo.ts`, e aqui fica só o despacho.
 *
 * Quem soma por `message.id` único (o JSONL do Claude repete o `usage` em cada
 * bloco da mesma mensagem) é o chamador: esta função pesa uma chamada.
 */
export function pesoDoUso(motor: 'CLAUDE', modelo: string | null, usage: UsoClaude): PesoDoUso;
export function pesoDoUso(motor: 'CODEX', modelo: string | null, usage: UsoCodex): PesoDoUso;
export function pesoDoUso(motor: 'OPENCODE', modelo: string | null, usage: UsoOpencode): PesoDoUso;
export function pesoDoUso(
  motor: Motor,
  modelo: string | null,
  usage: UsoClaude | UsoCodex | UsoOpencode,
): PesoDoUso;
export function pesoDoUso(
  motor: Motor,
  modelo: string | null,
  usage: UsoClaude | UsoCodex | UsoOpencode,
): PesoDoUso {
  switch (motor) {
    case 'CLAUDE':
      break;
    case 'CODEX': {
      const u = usage as UsoCodex;
      const cache = u.cached_input_tokens;
      const nova = Math.max(0, u.input_tokens - cache);
      return { peso: nova + PESO_CODEX.cache * cache + PESO_CODEX.saida * u.output_tokens, custoEquivUsd: null };
    }
    case 'OPENCODE':
      return pesoDoUsoOpencode(usage as UsoOpencode);
    default:
      return motorNaoTratado(motor);
  }
  // Daqui para baixo, só o CLAUDE: os outros dois saíram no `switch`.
  const tabela = precoDoModelo(modelo);
  if (tabela === null) return { peso: 0, custoEquivUsd: null };
  const u = usage as UsoClaude;
  const escrito = u.cache_creation_input_tokens ?? 0;
  const prompt = (u.input_tokens ?? 0) + (u.cache_read_input_tokens ?? 0) + escrito;
  const preco = tabela.degrau !== undefined && prompt > tabela.degrau.acimaDeTokens ? tabela.degrau.preco : tabela;
  // A parte de 1 h nunca passa do total escrito (dado torto não gera custo negativo de 5 min).
  const escrito1h = Math.min(Math.max(0, u.cache_creation_1h_input_tokens ?? 0), escrito);
  const custo =
    ((u.input_tokens ?? 0) * preco.entrada +
      (u.output_tokens ?? 0) * preco.saida +
      (u.cache_read_input_tokens ?? 0) * preco.cacheLido +
      (escrito - escrito1h) * preco.cacheEscrito +
      escrito1h * preco.entrada * FATOR_ESCRITA_1H) /
    MILHAO;
  return { peso: custo, custoEquivUsd: custo };
}
